import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Checkbox, Dropdown, Input, Tooltip, Typography, type InputRef, type MenuProps } from 'antd';
import {
  CaretDownOutlined,
  CaretRightOutlined,
  HolderOutlined,
  MessageOutlined,
  MoreOutlined,
  PaperClipOutlined,
  PlusOutlined,
  SubnodeOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
} from '@ant-design/icons';
import type { TaskItem } from '@crm/shared';
import type { TaskSort, TaskTree } from '../../features/tasks/view';
import { useProjectScope } from '../../features/projects/scope';
import { ProjectTag } from '../../components/ProjectSelect';
import { AssigneeCell, CompleteToggle, DueText, PriorityTag } from './TaskBits';
import { palette } from '../../app/theme';

export interface TaskGroup {
  /** section id, "none" or a My tasks bucket */
  key: string;
  title: ReactNode;
  /** top-level rows, in display order */
  tasks: TaskItem[];
  /** shown next to the title (e.g. the section's project) */
  badge?: ReactNode;
  /** section actions (rename, move, delete) */
  menu?: MenuProps['items'];
  /** quick add is offered */
  canAdd?: boolean;
  /** tasks may be dropped here */
  droppable?: boolean;
}

export interface TaskSelection {
  ids: Set<string>;
  toggle: (id: string, range?: boolean) => void;
  setMany: (ids: string[], on: boolean) => void;
  clear: () => void;
}

/** Multi-select over the rows in view; shift-click selects the range since the last click. */
export function useTaskSelection(order: string[]): TaskSelection {
  const [ids, setIds] = useState<Set<string>>(new Set());
  const last = useRef<string | null>(null);
  const toggle = useCallback(
    (id: string, range?: boolean) => {
      setIds((prev) => {
        const next = new Set(prev);
        const on = !prev.has(id);
        const from = range && last.current ? order.indexOf(last.current) : -1;
        const to = order.indexOf(id);
        if (from >= 0 && to >= 0) for (const x of order.slice(Math.min(from, to), Math.max(from, to) + 1)) on ? next.add(x) : next.delete(x);
        else on ? next.add(id) : next.delete(id);
        return next;
      });
      last.current = id;
    },
    [order],
  );
  const setMany = useCallback((list: string[], on: boolean) => setIds((prev) => {
    const next = new Set(prev);
    for (const id of list) on ? next.add(id) : next.delete(id);
    return next;
  }), []);
  const clear = useCallback(() => setIds(new Set()), []);
  // drop selections that left the view (deleted, filtered out)
  const visible = useMemo(() => new Set(order.filter((id) => ids.has(id))), [ids, order]);
  return { ids: visible, toggle, setMany, clear };
}

export interface TaskViewProps {
  groups: TaskGroup[];
  tree: TaskTree;
  /** manual order: rows can be dragged (also between groups) */
  draggable: boolean;
  onMove: (taskId: string, toGroup: string, orderedIds: string[]) => void;
  selection: TaskSelection;
  onOpen: (id: string) => void;
  onQuickAdd: (group: TaskGroup, title: string) => Promise<unknown>;
  /** the project column / label (all projects selected) */
  showProject: boolean;
  /** parent titles for subtasks shown as rows of their own (My tasks) */
  parents?: Map<string, string>;
  sort?: TaskSort;
  onSort?: (sort: TaskSort) => void;
  /** e.g. "Add section" */
  footer?: ReactNode;
}

/** Drag state shared by the list and the board: a drop reports the target group and its new order. */
function useGroupDrag(groups: TaskGroup[], onMove: TaskViewProps['onMove']) {
  const [active, setActive] = useState<TaskItem | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const byId = useMemo(() => new Map(groups.flatMap((g) => g.tasks.map((t) => [t.id, t] as const))), [groups]);
  const onDragStart = ({ active: a }: DragStartEvent) => setActive(byId.get(String(a.id)) ?? null);
  const onDragEnd = ({ active: a, over }: DragEndEvent) => {
    setActive(null);
    if (!over) return;
    const id = String(a.id);
    const overId = String(over.id);
    const from = groups.find((g) => g.tasks.some((t) => t.id === id));
    const target = overId.startsWith('group:') ? groups.find((g) => `group:${g.key}` === overId) : groups.find((g) => g.tasks.some((t) => t.id === overId));
    if (!from || !target || target.droppable === false) return;
    const list = target.tasks.map((t) => t.id).filter((x) => x !== id);
    let index = overId.startsWith('group:') ? list.length : target.tasks.findIndex((t) => t.id === overId);
    if (index < 0) index = list.length;
    if (from.key === target.key && target.tasks[index]?.id === id) return;
    list.splice(index, 0, id);
    onMove(id, target.key, list);
  };
  return { active, sensors, onDragStart, onDragEnd, onDragCancel: () => setActive(null) };
}

function QuickAdd({ onAdd, label = 'Add task…', compact }: { onAdd: (title: string) => Promise<unknown>; label?: string; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const input = useRef<InputRef>(null);
  const submit = async () => {
    const t = title.trim();
    if (!t) return setOpen(false);
    setBusy(true);
    try {
      await onAdd(t);
      setTitle('');
      input.current?.focus();
    } finally {
      setBusy(false);
    }
  };
  if (!open)
    return (
      <Button type="text" size="small" icon={<PlusOutlined />} onClick={() => setOpen(true)} style={{ color: palette.textSecondary, ...(compact ? { width: '100%', justifyContent: 'flex-start' } : {}) }}>
        {label}
      </Button>
    );
  return (
    <Input
      ref={input}
      autoFocus
      size="small"
      value={title}
      disabled={busy}
      placeholder="Task name, then Enter"
      aria-label="New task name"
      onChange={(e) => setTitle(e.target.value)}
      onPressEnter={() => void submit()}
      onBlur={() => !title.trim() && setOpen(false)}
      onKeyDown={(e) => e.key === 'Escape' && (setTitle(''), setOpen(false))}
      style={{ maxWidth: compact ? undefined : 420 }}
    />
  );
}

function Counts({ task }: { task: TaskItem }) {
  const bits: ReactNode[] = [];
  if (task.subtaskCount)
    bits.push(
      <Tooltip key="s" title={`${task.subtasksDone ?? 0} of ${task.subtaskCount} subtasks done`}>
        <span className="task-count">
          <SubnodeOutlined /> {task.subtasksDone ?? 0}/{task.subtaskCount}
        </span>
      </Tooltip>,
    );
  if (task.commentCount)
    bits.push(
      <span key="c" className="task-count" aria-label={`${task.commentCount} comments`}>
        <MessageOutlined /> {task.commentCount}
      </span>,
    );
  if (task.attachmentCount)
    bits.push(
      <span key="a" className="task-count" aria-label={`${task.attachmentCount} attachments`}>
        <PaperClipOutlined /> {task.attachmentCount}
      </span>,
    );
  return bits.length ? <>{bits}</> : null;
}

// -- list -------------------------------------------------------------------------------------------------------

const SORTABLE_COLUMNS: { key: TaskSort; label: string }[] = [
  { key: 'assignee', label: 'Assignee' },
  { key: 'due', label: 'Due date' },
  { key: 'priority', label: 'Priority' },
];

function gridColumns(showProject: boolean) {
  return `44px minmax(220px, 1fr) ${showProject ? '130px ' : ''}170px 110px 96px`;
}

interface RowProps {
  task: TaskItem;
  depth: number;
  props: TaskViewProps;
  expanded: Set<string>;
  toggleExpand: (id: string) => void;
  dragHandle?: ReactNode;
}

function Row({ task, depth, props, expanded, toggleExpand, dragHandle }: RowProps) {
  const { selection, onOpen, showProject, tree, parents } = props;
  const { byId } = useProjectScope();
  const kids = tree.children.get(task.id) ?? [];
  const open = expanded.has(task.id) || tree.autoExpand.has(task.id);
  const selected = selection.ids.has(task.id);
  const accent = showProject ? byId(task.projectId)?.primaryColor : null;
  const parentTitle = task.parentId ? parents?.get(task.parentId) : undefined;
  return (
    <>
      <div
        className={`task-row${selected ? ' task-row-selected' : ''}${task.completedAt ? ' task-row-done' : ''}`}
        style={{ gridTemplateColumns: gridColumns(showProject), boxShadow: accent ? `inset 3px 0 0 ${accent}` : undefined }}
        onClick={() => onOpen(task.id)}
        role="row"
      >
        <div className="task-cell task-cell-select" onClick={(e) => e.stopPropagation()}>
          {dragHandle ?? <span style={{ width: 14 }} />}
          <Checkbox checked={selected} aria-label={`Select "${task.title}"`} onClick={(e) => selection.toggle(task.id, (e as React.MouseEvent).shiftKey)} />
        </div>
        <div className="task-cell task-cell-title" style={{ paddingLeft: 4 + depth * 24 }}>
          {kids.length ? (
            <button
              type="button"
              className="task-caret"
              aria-label={open ? 'Hide subtasks' : 'Show subtasks'}
              aria-expanded={open}
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(task.id);
              }}
            >
              {open ? <CaretDownOutlined /> : <CaretRightOutlined />}
            </button>
          ) : (
            <span className="task-caret" />
          )}
          <CompleteToggle task={task} />
          <span className="task-title">
            {parentTitle && <span className="task-parent">{parentTitle} ›</span>}
            <span className="task-title-text">{task.title}</span>
          </span>
          <Counts task={task} />
        </div>
        {showProject && (
          <div className="task-cell">
            <ProjectTag projectId={task.projectId} />
          </div>
        )}
        <div className="task-cell">
          <AssigneeCell user={task.assignee} />
        </div>
        <div className="task-cell">
          <DueText task={task} empty={<Typography.Text type="secondary">—</Typography.Text>} />
        </div>
        <div className="task-cell">
          <PriorityTag priority={task.priority} />
        </div>
      </div>
      {open && kids.map((k) => <Row key={k.id} task={k} depth={depth + 1} props={props} expanded={expanded} toggleExpand={toggleExpand} />)}
    </>
  );
}

function SortableRow(p: RowProps & { enabled: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.task.id, disabled: !p.enabled });
  const handle = (
    <span
      className="task-drag"
      {...attributes}
      {...listeners}
      aria-label={`Drag "${p.task.title}"`}
      style={{ visibility: p.enabled ? undefined : 'hidden' }}
    >
      <HolderOutlined />
    </span>
  );
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.4 : 1, position: 'relative', zIndex: isDragging ? 2 : undefined }}>
      <Row {...p} dragHandle={handle} />
    </div>
  );
}

function ListGroup({ group, props, expanded, toggleExpand }: { group: TaskGroup; props: TaskViewProps; expanded: Set<string>; toggleExpand: (id: string) => void }) {
  const [collapsed, setCollapsed] = useState(false);
  const { setNodeRef, isOver } = useDroppable({ id: `group:${group.key}`, disabled: group.droppable === false });
  const allSelected = group.tasks.length > 0 && group.tasks.every((t) => props.selection.ids.has(t.id));
  return (
    <section className="task-group" aria-label={typeof group.title === 'string' ? group.title : undefined}>
      <div className="task-group-head">
        <button type="button" className="task-caret" aria-label={collapsed ? 'Expand section' : 'Collapse section'} aria-expanded={!collapsed} onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
        </button>
        <span className="task-group-title">{group.title}</span>
        {group.badge}
        <span className="task-group-count">{group.tasks.length}</span>
        {group.tasks.length > 0 && (
          <Tooltip title={allSelected ? 'Clear selection in this section' : 'Select all in this section'}>
            <Checkbox
              checked={allSelected}
              indeterminate={!allSelected && group.tasks.some((t) => props.selection.ids.has(t.id))}
              aria-label="Select all tasks in this section"
              onChange={(e) => props.selection.setMany(group.tasks.map((t) => t.id), e.target.checked)}
              style={{ marginLeft: 4 }}
            />
          </Tooltip>
        )}
        {group.menu && (
          <Dropdown menu={{ items: group.menu }} trigger={['click']}>
            <Button type="text" size="small" icon={<MoreOutlined />} aria-label="Section actions" />
          </Dropdown>
        )}
      </div>
      {!collapsed && (
        <div ref={setNodeRef} className={`task-group-body${isOver ? ' task-drop-over' : ''}`}>
          <SortableContext items={group.tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
            {group.tasks.map((t) => (
              <SortableRow key={t.id} task={t} depth={0} props={props} expanded={expanded} toggleExpand={toggleExpand} enabled={props.draggable && group.droppable !== false} />
            ))}
          </SortableContext>
          {!group.tasks.length && <div className="task-empty">{group.droppable === false ? 'Nothing here.' : 'No tasks. Add one, or drag one here.'}</div>}
          {group.canAdd && (
            <div className="task-add-row">
              <QuickAdd onAdd={(title) => props.onQuickAdd(group, title)} />
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export function TaskListView(props: TaskViewProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const drag = useGroupDrag(props.groups, props.onMove);
  const sortHeader = (key: TaskSort, label: string) => {
    if (!props.onSort) return label;
    const on = props.sort === key;
    return (
      <button type="button" className={`task-head-sort${on ? ' task-head-sort-on' : ''}`} onClick={() => props.onSort!(on ? 'manual' : key)} title={on ? 'Back to manual order' : `Sort by ${label.toLowerCase()}`}>
        {label} {on && (key === 'priority' || key === 'created' ? <ArrowDownOutlined /> : <ArrowUpOutlined />)}
      </button>
    );
  };
  return (
    <div className="task-list" role="table">
      <div className="task-head" style={{ gridTemplateColumns: gridColumns(props.showProject) }} role="row">
        <div />
        <div>{sortHeader('title', 'Task name')}</div>
        {props.showProject && <div>Project</div>}
        {SORTABLE_COLUMNS.map((c) => (
          <div key={c.key}>{sortHeader(c.key, c.label)}</div>
        ))}
      </div>
      <DndContext sensors={drag.sensors} collisionDetection={closestCorners} onDragStart={drag.onDragStart} onDragEnd={drag.onDragEnd} onDragCancel={drag.onDragCancel}>
        {props.groups.map((g) => (
          <ListGroup key={g.key} group={g} props={props} expanded={expanded} toggleExpand={toggleExpand} />
        ))}
        <DragOverlay>{drag.active && <div className="task-drag-ghost">{drag.active.title}</div>}</DragOverlay>
      </DndContext>
      {props.footer && <div style={{ padding: '12px 0' }}>{props.footer}</div>}
    </div>
  );
}

// -- board ------------------------------------------------------------------------------------------------------

function Card({ task, props }: { task: TaskItem; props: TaskViewProps }) {
  const selected = props.selection.ids.has(task.id);
  const parentTitle = task.parentId ? props.parents?.get(task.parentId) : undefined;
  return (
    <div
      className={`task-card${selected ? ' task-card-selected' : ''}${task.completedAt ? ' task-row-done' : ''}`}
      onClick={(e) => (e.shiftKey || e.metaKey || e.ctrlKey ? props.selection.toggle(task.id, e.shiftKey) : props.onOpen(task.id))}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <span style={{ paddingTop: 2 }}>
          <CompleteToggle task={task} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {parentTitle && <div className="task-parent">{parentTitle} ›</div>}
          <div className="task-title-text" style={{ whiteSpace: 'normal', fontWeight: 500 }}>
            {task.title}
          </div>
        </div>
        <span className="task-card-check" onClick={(e) => e.stopPropagation()} style={{ visibility: selected || props.selection.ids.size ? 'visible' : undefined }}>
          <Checkbox checked={selected} aria-label={`Select "${task.title}"`} onClick={(e) => props.selection.toggle(task.id, (e as React.MouseEvent).shiftKey)} />
        </span>
      </div>
      {(task.priority || props.showProject) && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          <PriorityTag priority={task.priority} />
          {props.showProject && <ProjectTag projectId={task.projectId} />}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, fontSize: 12, color: palette.textSecondary }}>
        {task.assignee ? <AssigneeCell user={task.assignee} showName={false} /> : null}
        <DueText task={task} />
        <span style={{ flex: 1 }} />
        <Counts task={task} />
      </div>
    </div>
  );
}

function SortableCard({ task, props, enabled }: { task: TaskItem; props: TaskViewProps; enabled: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id, disabled: !enabled });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.4 : 1, marginBottom: 8 }} {...attributes} {...listeners}>
      <Card task={task} props={props} />
    </div>
  );
}

function Column({ group, props }: { group: TaskGroup; props: TaskViewProps }) {
  const { setNodeRef, isOver } = useDroppable({ id: `group:${group.key}`, disabled: group.droppable === false });
  return (
    <div className={`task-column${isOver ? ' task-column-over' : ''}`}>
      <div className="task-column-head">
        <Typography.Text strong ellipsis style={{ flex: 1 }}>
          {group.title}
        </Typography.Text>
        {group.badge}
        <span className="task-group-count">{group.tasks.length}</span>
        {group.menu && (
          <Dropdown menu={{ items: group.menu }} trigger={['click']}>
            <Button type="text" size="small" icon={<MoreOutlined />} aria-label="Section actions" />
          </Dropdown>
        )}
      </div>
      <div ref={setNodeRef} className="task-column-body">
        <SortableContext items={group.tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {group.tasks.map((t) => (
            <SortableCard key={t.id} task={t} props={props} enabled={props.draggable && group.droppable !== false} />
          ))}
        </SortableContext>
        {!group.tasks.length && <div className="task-empty">{group.droppable === false ? 'Nothing here.' : 'Drag tasks here.'}</div>}
        {group.canAdd && <QuickAdd compact onAdd={(title) => props.onQuickAdd(group, title)} />}
      </div>
    </div>
  );
}

export function TaskBoardView(props: TaskViewProps) {
  const drag = useGroupDrag(props.groups, props.onMove);
  return (
    <DndContext sensors={drag.sensors} collisionDetection={closestCorners} onDragStart={drag.onDragStart} onDragEnd={drag.onDragEnd} onDragCancel={drag.onDragCancel}>
      <div className="task-board">
        {props.groups.map((g) => (
          <Column key={g.key} group={g} props={props} />
        ))}
        {props.footer && <div style={{ flex: '0 0 260px' }}>{props.footer}</div>}
      </div>
      <DragOverlay>{drag.active && <div className="task-card task-card-ghost"><span className="task-title-text">{drag.active.title}</span></div>}</DragOverlay>
    </DndContext>
  );
}
